import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { X } from "lucide-react"

interface TimePickerProps {
  selectedTimes: string[]
  onTimesChange: (times: string[]) => void
  disabled?: boolean
}

export function TimePicker({
  selectedTimes,
  onTimesChange,
  disabled = false,
}: TimePickerProps) {
  const [selectedHour, setSelectedHour] = useState("09")
  const [selectedMinute, setSelectedMinute] = useState("00")

  // Generate hours (00-23)
  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  )

  // Generate minutes (00, 15, 30, 45)
  const minutes = ["00", "15", "30", "45"]

  const addTime = () => {
    const newTime = `${selectedHour}:${selectedMinute}`
    if (!selectedTimes.includes(newTime)) {
      const updatedTimes = [...selectedTimes, newTime].sort()
      onTimesChange(updatedTimes)
    }
  }

  const removeTime = (timeToRemove: string) => {
    const updatedTimes = selectedTimes.filter((time) => time !== timeToRemove)
    onTimesChange(updatedTimes)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select
          value={selectedHour}
          onValueChange={setSelectedHour}
          disabled={disabled}>
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {hours.map((hour) => (
              <SelectItem key={hour} value={hour}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm">:</span>
        <Select
          value={selectedMinute}
          onValueChange={setSelectedMinute}
          disabled={disabled}>
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {minutes.map((minute) => (
              <SelectItem key={minute} value={minute}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          onClick={addTime}
          disabled={
            disabled ||
            selectedTimes.includes(`${selectedHour}:${selectedMinute}`)
          }
          size="sm"
          variant="outline">
          新增時間
        </Button>
      </div>

      {selectedTimes.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">已選擇的時間：</div>
          <div className="flex flex-wrap gap-2">
            {selectedTimes.map((time) => (
              <div
                key={time}
                className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm">
                <span>{time}</span>
                <button
                  type="button"
                  onClick={() => removeTime(time)}
                  disabled={disabled}
                  className="text-gray-500 hover:text-gray-700 disabled:opacity-50">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
